import { getAssetUrl } from "../../../utils/assetUrl";
import DraggableAnalysisRobot from "../../../components/DraggableAnalysisRobot";
import { GameScreenEmbed } from "../../../components/GameScreenEmbed";
import NhRobotHackLoading from "./NhRobotHackLoading";
import NhRobotHackResults from "./NhRobotHackResults";
import "./NhAnalysisLayout.css";

type ResultValues = {
  rounds: number;
  minBet: string;
};

export type NhAnalysisLayoutProps = {
  gameTitle: string;
  hallName?: string;
  gameImg: string;
  winPercent: number;
  gameScreenUrl: string;
  isScreenLoading: boolean;
  robotSpeech: string;
  robotVisible: boolean;
  autoMode: boolean;
  onAutoModeChange: (value: boolean) => void;
  onRobotVisibleChange: (value: boolean) => void;
  onBack: () => void;
  onSeeResults: () => void;
  onStopHack: () => void;
  onAddCoins: () => void;
  onVipActivate: () => void;
  onVipStop: () => void;
  vipHackActive: boolean;
  vipBusy: boolean;
  isSpinning: boolean;
  showHackInput: boolean;
  showHackLoading: boolean;
  showHackResults: boolean;
  manualValues: ResultValues;
  autoValues: ResultValues;
  timeSlotText: string;
  refreshRemainingSec: number;
  hackProgress: number;
  hackBalance: string;
  onHackBalanceChange: (value: string) => void;
  onConfirmHack: () => void;
};

export default function NhAnalysisLayout({
  gameTitle,
  hallName = "GAME",
  gameImg,
  winPercent,
  gameScreenUrl,
  isScreenLoading,
  robotSpeech,
  robotVisible,
  autoMode,
  onAutoModeChange,
  onBack,
  onSeeResults,
  onStopHack,
  isSpinning,
  showHackLoading,
  showHackResults,
  manualValues,
  autoValues,
  timeSlotText,
  refreshRemainingSec,
  hackProgress,
  hackBalance,
  onHackBalanceChange,
}: NhAnalysisLayoutProps) {
  const title = gameTitle.trim() || "QUYẾT CHIẾN TIỀN THƯỞNG";
  const busy = isSpinning || showHackLoading;

  return (
    <div className="nh-analysis nh-analysis--figma">
      <div className="nh-analysis__body">
        <section className="nh-analysis__workspace">
          <button type="button" className="nh-analysis__figma-back" onClick={onBack}>
            ◂ QUAY LẠI
          </button>

          <div className="nh-analysis__game">
            <img
              src={gameImg}
              alt={title}
              onError={(event) => {
                event.currentTarget.src = getAssetUrl("/assets/nohu.gif");
              }}
            />
            <div className="nh-analysis__ring">{winPercent}%</div>
          </div>
          <h1 className="nh-analysis__figma-title">{title}</h1>
          <p className="nh-analysis__module">{`// SẢNH ${hallName} //`}</p>

          <div className="nh-analysis__strategies">
            <button
              type="button"
              className={!autoMode ? "is-active" : ""}
              onClick={() => onAutoModeChange(false)}
            >
              <strong>Quay mồi</strong>
              <small>
                {showHackResults
                  ? `${manualValues.rounds} vòng - Mức cược ${manualValues.minBet}`
                  : "Chờ phân tích"}
              </small>
            </button>
            <button
              type="button"
              className={autoMode ? "is-active" : ""}
              onClick={() => onAutoModeChange(true)}
            >
              <strong>Quay Auto</strong>
              <small>
                {showHackResults
                  ? `${autoValues.rounds} vòng - Mức cược ${autoValues.minBet}`
                  : "Chờ phân tích"}
              </small>
            </button>
            <div>
              <strong>Tỷ lệ scatter</strong>
              <small>{showHackResults ? "Khung giờ vàng" : "Chờ phân tích"}</small>
            </div>
            <div className="nh-analysis__strategy-time">
              <strong>Thời gian hiệu lực</strong>
              <small>{showHackResults ? timeSlotText : "Chờ phân tích"}</small>
            </div>
          </div>

          <p className="nh-analysis__hint">
            NHẬP SỐ ĐIỂM VỐN HIỆN TẠI CỦA BẠN TRONG GAME (VÍ DỤ: 1000)
          </p>

          <label className="nh-analysis__capital">
            <input
              aria-label="Số điểm vốn"
              inputMode="numeric"
              type="text"
              placeholder="Số điểm vốn (VD: 1000)"
              value={hackBalance}
              disabled={busy}
              onChange={(event) => onHackBalanceChange(event.target.value.replace(/\D/g, ""))}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onSeeResults();
                }
              }}
            />
          </label>

          <button
            type="button"
            className="nh-analysis__scan"
            disabled={busy}
            onClick={showHackResults ? onStopHack : onSeeResults}
          >
            {showHackLoading
              ? "ĐANG QUÉT DỮ LIỆU..."
              : showHackResults
                ? "DỪNG HACK"
                : "HACK THÔNG SỐ"}
          </button>

          {robotVisible && !showHackLoading && !showHackResults ? (
            <img
              className="nh-analysis__mascot"
              src={getAssetUrl("/assets/robot.gif")}
              alt=""
            />
          ) : null}
        </section>

        <aside className="nh-analysis__external">
          <GameScreenEmbed
            url={gameScreenUrl}
            name="GG88"
            title={`Game ${title}`}
            loading={isScreenLoading}
          />
        </aside>
      </div>

      {robotVisible && (showHackLoading || showHackResults) ? (
        <DraggableAnalysisRobot
          message={robotSpeech}
          imageSrc="/assets/robot.gif"
          className="nh-analysis__robot"
          hideBubble={showHackLoading || showHackResults}
          topSlot={
            showHackLoading ? (
              <NhRobotHackLoading progress={hackProgress} />
            ) : showHackResults ? (
              <NhRobotHackResults
                gameTitle={title}
                winPercent={winPercent}
                manualValues={manualValues}
                autoValues={autoValues}
                timeSlotText={timeSlotText}
                refreshRemainingSec={refreshRemainingSec}
              />
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
